<script setup>
// NOTICE: KAPTION-INTEGRATION
// new-conversation-modal.GALLERY.2 / .4 / .5 — the same in-flow creator is
// shared by New message and the reply-box template picker.
import { reactive, computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import KaptionWhatsAppPreviewEnhanced from 'dashboard/components-next/Campaigns/KaptionWhatsAppPreviewEnhanced.vue';
import KaptionMediaUpload from 'dashboard/components-next/Campaigns/KaptionMediaUpload.vue';
import WhatsappTemplatesAPI from 'dashboard/api/whatsappTemplates';

const props = defineProps({ inbox: { type: Object, required: true } });
const emit = defineEmits(['created', 'back']);
const store = useStore();
const { t, locale } = useI18n();
const form = reactive({
  name: '',
  language: 'es_AR',
  category: 'MARKETING',
  headerType: 'none',
  header: '',
  headerMediaUrl: '',
  body: '',
  footer: '',
  buttons: [],
});
const submitting = ref(false);
const variableExample = '{{name}}';
const urlPlaceholder = 'https://';
const maxButtons = 3;
const bodyLimit = 1024;
// Chatwoot's global form rules outrank utility classes on typed inputs;
// `reset-base` opts out so every field in the creator looks the same.
const fieldClass =
  'reset-base block w-full h-9 px-3 m-0 text-sm rounded-lg border border-solid border-n-weak bg-n-solid-1 text-n-slate-12 placeholder:text-n-slate-10 outline-none transition-colors hover:border-n-strong focus:border-n-brand focus:ring-1 focus:ring-n-brand';
const selectClass = `${fieldClass} appearance-none bg-none pe-9 cursor-pointer`;
const labelClass =
  'flex flex-col gap-1.5 m-0 text-xs font-medium leading-4 text-n-slate-11';

// Meta template names: lowercase letters, digits and underscores. Normalize as
// the agent types instead of rejecting "Order update" later.
watch(
  () => form.name,
  name => {
    const normalized = name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[\s-]+/g, '_')
      .replace(/[^a-z0-9_]/g, '');
    if (normalized !== name) form.name = normalized;
  }
);
const nameInvalid = computed(
  () => form.name.length > 0 && !/^[a-z][a-z0-9_]*$/.test(form.name)
);

const COMMON_LANGUAGES = [
  'es_AR',
  'es_MX',
  'es_ES',
  'es',
  'pt_BR',
  'en_US',
  'en_GB',
  'en',
  'fr',
  'de',
  'it',
];
const languageOptions = computed(() => {
  const used = (props.inbox?.message_templates || [])
    .map(template => template?.language)
    .filter(Boolean);
  let names = null;
  try {
    names = new Intl.DisplayNames([String(locale.value).replace('_', '-')], {
      type: 'language',
    });
  } catch {
    // Fall back to raw codes when the runtime lacks Intl.DisplayNames.
  }
  return [...new Set([form.language, ...used, ...COMMON_LANGUAGES])].map(
    code => {
      let name = '';
      try {
        name = names?.of(code.replace('_', '-')) || '';
      } catch {
        // Unknown codes keep their raw label.
      }
      return { value: code, label: name ? `${name} (${code})` : code };
    }
  );
});
const buttonTypes = ['QUICK_REPLY', 'URL', 'PHONE_NUMBER'];
const categoryAdvice = ref(null);
let adviceRequest = 0;
const categoryContent = () => ({
  header: form.headerType === 'text' ? form.header : '',
  body: form.body,
  footer: form.footer,
  button_types: form.buttons.map(button => button.type),
  button_texts: form.buttons.map(button => button.text),
});
const definitelyMarketing = advice =>
  advice?.category === 'MARKETING' && advice.confidence !== 'low';

// new-conversation-modal.GALLERY.9 — ask the same analyzer used by the partner
// template builder. A media header is deliberately absent from this decision.
watch(
  () => [
    form.header,
    form.body,
    form.footer,
    ...form.buttons.flatMap(b => [b.text, b.type]),
  ],
  (_content, _previous, onCleanup) => {
    adviceRequest += 1;
    const request = adviceRequest;
    categoryAdvice.value = null;
    if (!form.body.trim()) return;
    const timeout = setTimeout(async () => {
      try {
        const { data } = await WhatsappTemplatesAPI.suggestCategory(
          props.inbox.id,
          categoryContent()
        );
        if (request !== adviceRequest) return;
        categoryAdvice.value = data;
        if (definitelyMarketing(data)) {
          form.category = 'MARKETING';
        }
      } catch {
        // Category advice is optional; Meta validates the selected category.
      }
    }, 350);
    onCleanup(() => clearTimeout(timeout));
  }
);

const categoryAdviceText = computed(() => {
  if (!categoryAdvice.value)
    return t('WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.CATEGORY_HELP');
  if (categoryAdvice.value.category === 'UTILITY')
    return t('NEW_CONVERSATION_MODAL.TEMPLATE.UTILITY_POSSIBLE');
  return categoryAdvice.value.confidence === 'low'
    ? t('NEW_CONVERSATION_MODAL.TEMPLATE.GENERIC_MARKETING_RISK')
    : t('NEW_CONVERSATION_MODAL.TEMPLATE.PROMOTIONAL_MARKETING');
});

const buttonValid = button => {
  if (!buttonTypes.includes(button.type) || !button.text.trim()) return false;
  if (button.type === 'URL') {
    return (
      /^https?:\/\/\S+$/i.test(button.url.trim()) && !button.url.includes('{{')
    );
  }
  if (button.type === 'PHONE_NUMBER') {
    return /^\+[1-9]\d{5,19}$/.test(button.phone_number.trim());
  }
  return true;
};

const valid = computed(
  () =>
    /^[a-z][a-z0-9_]*$/.test(form.name) &&
    form.language.trim().length > 0 &&
    form.body.trim().length > 0 &&
    !/\{\{\d+\}\}/.test(form.body) &&
    (form.headerType !== 'text' ||
      (form.header.trim().length > 0 && !form.header.includes('{{'))) &&
    (!['image', 'video', 'document'].includes(form.headerType) ||
      /^https:\/\/\S+$/i.test(form.headerMediaUrl)) &&
    !form.footer.includes('{{') &&
    form.buttons.length <= maxButtons &&
    form.buttons.every(buttonValid)
);

const templateButtons = computed(() =>
  form.buttons.map(button => ({
    type: button.type,
    text: button.text,
    ...(button.type === 'URL' ? { url: button.url } : {}),
    ...(button.type === 'PHONE_NUMBER'
      ? { phone_number: button.phone_number }
      : {}),
  }))
);

const previewTemplate = computed(() => ({
  name: form.name || 'preview',
  language: form.language,
  category: form.category,
  status: 'DRAFT',
  components: [
    ...(form.headerType === 'text' && form.header
      ? [{ type: 'HEADER', format: 'TEXT', text: form.header }]
      : []),
    ...(['image', 'video', 'document'].includes(form.headerType)
      ? [
          {
            type: 'HEADER',
            format: form.headerType.toUpperCase(),
            example: { header_handle: [form.headerMediaUrl] },
          },
        ]
      : []),
    { type: 'BODY', text: form.body },
    ...(form.footer ? [{ type: 'FOOTER', text: form.footer }] : []),
    ...(form.buttons.length
      ? [{ type: 'BUTTONS', buttons: templateButtons.value }]
      : []),
  ],
}));

const addButton = () => {
  if (form.buttons.length < maxButtons) {
    form.buttons.push({
      type: 'QUICK_REPLY',
      text: '',
      url: '',
      phone_number: '',
    });
  }
};

const submit = async () => {
  if (!valid.value || submitting.value) return;
  submitting.value = true;
  try {
    // Recheck at submission so a fast click cannot outrun the debounced hint.
    try {
      const { data } = await WhatsappTemplatesAPI.suggestCategory(
        props.inbox.id,
        categoryContent()
      );
      categoryAdvice.value = data;
      if (definitelyMarketing(data)) form.category = 'MARKETING';
    } catch {
      // The provider remains the final authority if the hint is unavailable.
    }
    const result = await store.dispatch('whatsappTemplates/create', {
      inboxId: props.inbox.id,
      templateData: {
        name: form.name,
        language: form.language,
        category: form.category,
        body: form.body,
        header_type: form.headerType,
        header_text:
          form.headerType === 'text' ? form.header || undefined : undefined,
        header_media_url: ['image', 'video', 'document'].includes(
          form.headerType
        )
          ? form.headerMediaUrl
          : undefined,
        footer: form.footer || undefined,
        buttons: form.buttons.length ? templateButtons.value : undefined,
      },
    });
    store.dispatch('inboxes/get').catch(() => {});
    emit('created', {
      ...previewTemplate.value,
      id: result?.template?.id,
      category: result?.template?.category || form.category,
      status: result?.template?.status || 'PENDING',
      parameter_format: 'NAMED',
    });
  } catch (error) {
    useAlert(error?.response?.data?.error || t('NEW_CONVERSATION_MODAL.ERROR'));
  } finally {
    submitting.value = false;
  }
};
</script>

<template>
  <form
    class="flex flex-col gap-4"
    data-testid="create-template-step"
    @submit.prevent="submit"
  >
    <div>
      <h3 class="m-0 text-sm font-semibold text-n-slate-12">
        {{ t('NEW_CONVERSATION_MODAL.TEMPLATE.CREATE') }}
      </h3>
      <p class="m-0 mt-0.5 text-xs leading-5 text-n-slate-10">
        {{ t('NEW_CONVERSATION_MODAL.TEMPLATE.CREATE_HINT') }}
      </p>
    </div>

    <div
      class="grid grid-cols-1 items-start gap-6 md:grid-cols-[minmax(0,1fr)_20rem]"
    >
      <div class="flex flex-col gap-4 min-w-0">
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label :class="labelClass">
            {{ t('NEW_CONVERSATION_MODAL.TEMPLATE.NAME') }}
            <input
              v-model="form.name"
              data-testid="create-template-name"
              type="text"
              :class="[fieldClass, { '!border-n-ruby-8': nameInvalid }]"
              maxlength="512"
              autocomplete="off"
              :placeholder="t('NEW_CONVERSATION_MODAL.TEMPLATE.NAME_EXAMPLE')"
            />
            <span
              class="font-normal text-[11px] leading-4"
              :class="nameInvalid ? 'text-n-ruby-11' : 'text-n-slate-10'"
            >
              {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.NAME_HELP') }}
            </span>
          </label>
          <label :class="labelClass">
            {{ t('NEW_CONVERSATION_MODAL.TEMPLATE.LANGUAGE') }}
            <span class="relative block">
              <select
                v-model="form.language"
                data-testid="create-template-language"
                :class="selectClass"
              >
                <option
                  v-for="option in languageOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
              <span
                class="absolute -translate-y-1/2 pointer-events-none end-3 top-1/2 i-lucide-chevron-down size-4 text-n-slate-10"
              />
            </span>
          </label>
        </div>

        <div class="flex flex-col gap-1.5">
          <label :class="labelClass">
            {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.CATEGORY_LABEL') }}
            <span class="relative block">
              <select
                v-model="form.category"
                data-testid="create-template-category"
                :class="selectClass"
              >
                <option value="MARKETING">
                  {{
                    t('WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.CATEGORY_MARKETING')
                  }}
                </option>
                <option
                  value="UTILITY"
                  :disabled="definitelyMarketing(categoryAdvice)"
                >
                  {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.CATEGORY_UTILITY') }}
                </option>
              </select>
              <span
                class="absolute -translate-y-1/2 pointer-events-none end-3 top-1/2 i-lucide-chevron-down size-4 text-n-slate-10"
              />
            </span>
          </label>
          <p
            data-testid="category-advice"
            class="flex items-start gap-1.5 m-0 text-[11px] leading-4"
            :class="
              definitelyMarketing(categoryAdvice)
                ? 'text-n-amber-11'
                : 'text-n-slate-10'
            "
          >
            <span
              class="mt-px shrink-0 size-3.5"
              :class="
                definitelyMarketing(categoryAdvice)
                  ? 'i-lucide-megaphone'
                  : 'i-lucide-info'
              "
            />
            <span>{{ categoryAdviceText }}</span>
          </p>
        </div>

        <div class="flex flex-col gap-2">
          <label :class="labelClass">
            {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.HEADER_LABEL') }}
            <span class="relative block">
              <select
                id="create-template-header-type"
                v-model="form.headerType"
                data-testid="create-template-header-type"
                :class="selectClass"
              >
                <option value="none">
                  {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.HEADER_TYPE_NONE') }}
                </option>
                <option value="text">
                  {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.HEADER_TYPE_TEXT') }}
                </option>
                <option value="image">
                  {{
                    t('WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.HEADER_TYPE_IMAGE')
                  }}
                </option>
                <option value="video">
                  {{
                    t('WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.HEADER_TYPE_VIDEO')
                  }}
                </option>
                <option value="document">
                  {{
                    t('WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.HEADER_TYPE_DOCUMENT')
                  }}
                </option>
              </select>
              <span
                class="absolute -translate-y-1/2 pointer-events-none end-3 top-1/2 i-lucide-chevron-down size-4 text-n-slate-10"
              />
            </span>
          </label>
          <input
            v-if="form.headerType === 'text'"
            v-model="form.header"
            data-testid="create-template-header-text"
            type="text"
            maxlength="60"
            :aria-label="t('WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.HEADER_LABEL')"
            :placeholder="
              t('WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.HEADER_TEXT_PLACEHOLDER')
            "
            :class="fieldClass"
          />
          <KaptionMediaUpload
            v-if="['image', 'video', 'document'].includes(form.headerType)"
            v-model="form.headerMediaUrl"
            :media-type="form.headerType"
          />
        </div>

        <div class="flex flex-col gap-1.5">
          <label :class="labelClass">
            <span class="flex items-center justify-between gap-2">
              {{ t('NEW_CONVERSATION_MODAL.TEMPLATE.BODY') }}
              <span class="font-normal tabular-nums text-n-slate-10">
                {{ form.body.length }}/{{ bodyLimit }}
              </span>
            </span>
            <textarea
              v-model="form.body"
              :maxlength="bodyLimit"
              rows="5"
              data-testid="create-template-body"
              class="!h-auto min-h-[7.5rem] py-2 resize-y"
              :class="[fieldClass]"
            />
          </label>
          <p class="m-0 text-[11px] leading-4 text-n-slate-10">
            {{
              t('NEW_CONVERSATION_MODAL.TEMPLATE.VARIABLE_HINT', {
                variable: variableExample,
              })
            }}
          </p>
        </div>

        <label :class="labelClass">
          {{ t('NEW_CONVERSATION_MODAL.TEMPLATE.FOOTER') }}
          <input
            v-model="form.footer"
            type="text"
            maxlength="60"
            data-testid="create-template-footer"
            :placeholder="
              t('WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.FOOTER_PLACEHOLDER')
            "
            :class="fieldClass"
          />
        </label>

        <div class="flex flex-col gap-2">
          <div class="flex items-center justify-between gap-2">
            <span class="text-xs font-medium text-n-slate-11">
              {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.BUTTONS_LABEL') }}
            </span>
            <span class="text-[11px] tabular-nums text-n-slate-10">
              {{ form.buttons.length }}/{{ maxButtons }}
            </span>
          </div>
          <div
            v-for="(button, index) in form.buttons"
            :key="index"
            class="flex flex-col gap-2 p-2.5 border rounded-lg border-n-weak bg-n-alpha-black2"
            data-testid="create-template-button-row"
          >
            <div class="flex items-center gap-2">
              <span class="relative block w-48 shrink-0">
                <select
                  v-model="button.type"
                  :aria-label="
                    t('WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.BUTTON_TYPE_LABEL')
                  "
                  :class="selectClass"
                >
                  <option value="QUICK_REPLY">
                    {{
                      t(
                        'WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.BUTTON_TYPE_QUICK_REPLY'
                      )
                    }}
                  </option>
                  <option value="URL">
                    {{
                      t('WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.BUTTON_TYPE_URL')
                    }}
                  </option>
                  <option value="PHONE_NUMBER">
                    {{
                      t('WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.BUTTON_TYPE_PHONE')
                    }}
                  </option>
                </select>
                <span
                  class="absolute -translate-y-1/2 pointer-events-none end-3 top-1/2 i-lucide-chevron-down size-4 text-n-slate-10"
                />
              </span>
              <input
                v-model="button.text"
                type="text"
                maxlength="25"
                :aria-label="
                  t(
                    'WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.BUTTON_TEXT_PLACEHOLDER'
                  )
                "
                :placeholder="
                  t(
                    'WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.BUTTON_TEXT_PLACEHOLDER'
                  )
                "
                class="min-w-0 flex-1"
                :class="[fieldClass]"
              />
              <button
                type="button"
                :aria-label="
                  t('WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.REMOVE_BUTTON')
                "
                :title="t('WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.REMOVE_BUTTON')"
                class="flex items-center justify-center p-0 rounded-md shrink-0 size-9 text-n-slate-10 hover:bg-n-alpha-black2 hover:text-n-ruby-11"
                @click="form.buttons.splice(index, 1)"
              >
                <span class="i-lucide-trash-2 size-4" />
              </button>
            </div>
            <template v-if="button.type === 'URL'">
              <input
                v-model="button.url"
                type="url"
                maxlength="2000"
                :aria-label="
                  t('WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.BUTTON_TYPE_URL')
                "
                :placeholder="urlPlaceholder"
                :class="fieldClass"
              />
              <span class="text-[11px] leading-4 text-n-slate-10">
                {{ t('NEW_CONVERSATION_MODAL.TEMPLATE.STATIC_URL_HINT') }}
              </span>
            </template>
            <input
              v-if="button.type === 'PHONE_NUMBER'"
              v-model="button.phone_number"
              type="tel"
              maxlength="20"
              :aria-label="
                t('WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.BUTTON_TYPE_PHONE')
              "
              :placeholder="
                t('WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.BUTTON_PHONE_PLACEHOLDER')
              "
              :class="fieldClass"
            />
          </div>
          <button
            v-if="form.buttons.length < maxButtons"
            type="button"
            data-testid="create-template-add-button"
            class="flex items-center justify-center w-full gap-1.5 m-0 text-xs font-medium border border-dashed rounded-lg h-9 border-n-strong text-n-slate-11 hover:border-n-brand hover:text-n-brand"
            @click="addButton"
          >
            <span class="i-lucide-plus size-3.5" />
            {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.ADD_BUTTON') }}
          </button>
        </div>
      </div>

      <div
        class="flex flex-col gap-2 min-w-0 md:sticky md:top-0"
        data-testid="create-template-preview"
      >
        <p class="m-0 text-xs font-medium text-n-slate-11">
          {{ t('NEW_CONVERSATION_MODAL.COMPOSE.PREVIEW_TITLE') }}
        </p>
        <KaptionWhatsAppPreviewEnhanced
          :template="previewTemplate"
          mode="render"
          :company-name="inbox.name"
          class="h-[min(34rem,calc(100vh-18rem))] min-h-[24rem] border border-n-weak"
        />
        <p class="m-0 text-[11px] text-n-slate-10">
          {{ t('NEW_CONVERSATION_MODAL.COMPOSE.PREVIEW_HINT') }}
        </p>
      </div>
    </div>

    <div
      class="sticky z-10 flex items-center justify-between gap-3 pt-3 pb-5 -mx-6 px-6 -mb-5 -bottom-5 border-t border-n-weak bg-n-solid-1"
    >
      <Button
        type="button"
        :label="t('NEW_CONVERSATION_MODAL.BACK')"
        icon="i-lucide-arrow-left"
        variant="ghost"
        color="slate"
        size="sm"
        data-testid="create-template-back"
        @click="emit('back')"
      />
      <Button
        type="submit"
        :label="t('NEW_CONVERSATION_MODAL.TEMPLATE.SUBMIT')"
        :is-loading="submitting"
        :disabled="!valid"
        size="sm"
        data-testid="create-template-submit"
      />
    </div>
  </form>
</template>
